import React, {useState} from 'react';
import {Divider, IconButton, Items, SidebarDrawer as Drawer, Chip, Button} from '../StyledComponents';
import {SidebarProps} from './types';
import {List} from '@mui/material';
import logo from "../../assets/icons/bl-logo.svg";
import logoSm from "../../assets/icons/backuplabs-logo.svg";
import {dashboardSection, integrationsSection, servicesSection} from './dashboardItems'
import {NavLink, useNavigate} from 'react-router';
import {useTranslation} from "react-i18next";
import {Item, Title} from "./styledComponents";
import {iniFrame} from "../../serviceWorker";
import {Plus} from "react-feather";
import {useAppSelector} from "../../store/hooks";
import {
  getRawSubscriptions,
  getUserSelector,
  isAuthorized,
  isHasProPlan,
  isSubUser,
  userRoles
} from "../../store/selectors";
import {SidebarLeft, SidebarRight} from "iconsax-reactjs";
import AuthorizedUserItem from "../Header/AuthorizedUserItem/AuthorizedUserItem";
import {IUser} from "../../models/models";
import {PlatformsState} from "../../store/reducers/platform";
import {DRAWER_WIDTH, ETrialStatus, SMALL_DRAWER_WIDTH} from "../../models/consts";
import './style.scss';

type SidebarProps = {
  isOpen: boolean;
  isMobile: boolean;
  setIsOpen: () => void;
};

const Sidebar: React.FC<SidebarProps> = ({isOpen, setIsOpen, isMobile}) => {
  const navigate = useNavigate();
  const {t: translate} = useTranslation();
  const isUserSub = useAppSelector(isSubUser);
  const isAuth = useAppSelector(isAuthorized);
  const user: IUser | null = useAppSelector(getUserSelector);
  const hasProPlan = useAppSelector(isHasProPlan);
  const roles = useAppSelector(userRoles);
  const platforms: PlatformsState | null = useAppSelector(getRawSubscriptions);
  const platformsName = Object.keys(platforms)
  const [protectedApps, setProtectedApps] = useState<string[]>([]);
  const hasMoreTrial = Object.values(platforms)?.filter(item=>item.trial?.trialStatus === 'active')?.length

  const openContact = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!iniFrame()) {
      window.open('https://support.backuplabs.io/hc/en-gb', '_blank')
    } else {
      navigate('/contact')
    }
  }

  return (
    <Drawer
      id='sidebar'
      className={isOpen ? '' : 'collapsed-drawer'}
      // variant={isMobile ? 'temporary' : 'permanent'}
      variant='permanent'
      open={isOpen}
      slotProps={{paper: {style: {width: isOpen ? DRAWER_WIDTH : SMALL_DRAWER_WIDTH}}}}
    >
      <div className={isOpen ? 'logoBlock' : 'logoBlock collapsed'}>
        <div className='brandBlock' onClick={() => {
          if (user?.hadEverAnySubscriptions) {
            navigate('/');
          } else {
            navigate('/app-connector')
          }
        }}>
          {isOpen ?
            <img key='big' className="logo" src={logo} width={154} loading="lazy"/> :
            <img key='small' className="logo" src={logoSm} width={36} loading="lazy"/>
          }
        </div>
        <div className='collapse-btn' onClick={setIsOpen}>
          <IconButton
            color="inherit"
            aria-label="Open drawer"
            className='open-drawer'
          >
            {isOpen ? <SidebarLeft/> : <SidebarRight/>}
          </IconButton>
          {isOpen && 'Collapse'}
        </div>
      </div>
      <Divider/>
      <List disablePadding className='dashboard-section'>
        <div className='sidebar-block-title'>
          <Title variant="subtitle2">Main</Title>
        </div>
        <Items isOpen={isOpen}>
          {dashboardSection.map((item) => {
            const Icon = item.icon;
            if (((hasProPlan && item.href === '/audit') || item.href !== '/audit') && (!isUserSub || isUserSub && item.href !== '/app-connector')) {
              return (
                <Item
                  component={NavLink}
                  key={item.href}
                  to={item.href}
                >
                  <Icon/>
                  <Title depth={0}>
                    {isOpen && translate(item.title)}
                  </Title>
                </Item>
              )
            }
          })}
        </Items>
      </List>
      <Divider/>
      <List disablePadding className='added-app-block'>
        <div className='sidebar-block-title'>
          <Title variant="subtitle2">
            {isOpen ? `Protected Apps ${!!protectedApps.length ? `(${protectedApps.length})` : ''}` : 'Apps'}
          </Title>
          {!isUserSub && isOpen && (
            <div className='sidebar-block-title-icon'>
              <Plus className='feather-icon-sidebar' onClick={() => navigate('/app-connector')}/>
            </div>
          )}
        </div>
        <Items isOpen={isOpen} className='added-app-list'>
          <div className='added-app-container'>
          {user && platforms && integrationsSection.map((item) => {
            const Icon = item.icon;
            const curPlatform = platformsName.find(platName => item.href.indexOf(platName) !== -1) || ''

            const isAdded = platforms[curPlatform]?.trial?.trialStatus && platforms[curPlatform]?.trial?.trialStatus !== ETrialStatus.NOT_STARTED
            if (((!isUserSub && isAdded) || !curPlatform?.length || (hasProPlan && isUserSub && roles?.some(i => i.includes(curPlatform))))) {
              if (isAdded && !protectedApps.includes(curPlatform)) setProtectedApps([...protectedApps, curPlatform])
              return (
                <Item
                  component={NavLink}
                  key={item.href}
                  to={item.href}
                >
                  <Icon/>
                  <Title depth={0}>
                    {isOpen && translate(item.title)}
                  </Title>
                </Item>
              )
            }
          })}
          </div>
          {!isUserSub && !isOpen && (
            <div className='sidebar-block-title'>
              <div className='sidebar-block-title-icon'>
                <Plus className='feather-icon-sidebar' onClick={() => navigate('/app-connector')}/>
              </div>
            </div>
          )}
          {!isUserSub && !!hasMoreTrial && (
            <div className='trial-sidebar'>
              <Chip label='Free trial' className='active'/>
              <div className='trial-sidebar-title'>You have active Trial</div>
              <div className='trial-sidebar-sub'>No credit card required</div>
              <Button variant='contained' color='primary' onClick={()=>navigate('/subscriptions')}>Subscribe Now</Button>
            </div>
          )}
        </Items>
      </List>

      <Divider/>
      <List disablePadding className='service-section'>
        <Items isOpen={isOpen}>
          {servicesSection.map((item) => {
            const Icon = item.icon;
            if (!isUserSub || (isUserSub && item.href !== '/subscriptions' && item.href !== '/payment-history')) {
              return (
                <Item
                  component={NavLink}
                  key={item.href}
                  to={item.href}
                  onClick={(e) => {
                    if (item.href === 'contact') openContact(e)
                  }}
                >
                  <Icon/>
                  <Title depth={0}>
                    {isOpen && translate(item.title)}
                  </Title>
                </Item>
              )
            }
          })}
        </Items>
      </List>
      <Divider/>
      <nav className="user-menu">
        {isAuth && <AuthorizedUserItem isOpen={isOpen}/>}
      </nav>
    </Drawer>
  );
};

export default (Sidebar);
